import React, { useState, useEffect, useRef } from 'react';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import { toast } from './Toast';
import './EnquiryFormModal.css';

function EnquiryFormModal({ enquiry, users, onClose, onSuccess }) {
  const { isCompanyAdmin } = useAuth();
  const phoneRef = useRef(null);
  const [formData, setFormData] = useState({
    customerName: '',
    phone: '91',
    email: '',
    source: '',
    projectRequirement: '',
    status: 'NEW',
    assignedToId: '',
    branchId: '',
    alternatePhone: '91',
    careOf: '',
    secondContact: '',
    secondContactName: '',
    notes: '',
  });
  const [branches, setBranches] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [countryCode, setCountryCode] = useState('91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [altCountryCode, setAltCountryCode] = useState('91');
  const [altPhoneNumber, setAltPhoneNumber] = useState('');

  useEffect(() => {
    if (isCompanyAdmin()) fetchBranches();
  }, []);

  useEffect(() => {
    if (enquiry) {
      setFormData({
        customerName: enquiry.customerName,
        phone: enquiry.phone || '91',
        email: enquiry.email || '',
        source: enquiry.source || '',
        projectRequirement: enquiry.projectRequirement,
        status: enquiry.status,
        assignedToId: enquiry.assignedTo ? enquiry.assignedTo.id : '',
        branchId: enquiry.branchId || '',
        alternatePhone: enquiry.alternatePhone || '91',
        careOf: enquiry.careOf || '',
        secondContact: enquiry.secondContact || '',
        secondContactName: enquiry.secondContactName || '',
        notes: enquiry.notes || '',
      });

      let fullPhone = enquiry.phone || '';
      let code = '91';
      let num = '';
      if (fullPhone === '91' || !fullPhone) {
        code = '91';
        num = '';
      } else if (fullPhone.startsWith('+')) {
        const match = fullPhone.match(/^\+(\d{1,4})/);
        if (match) {
          code = '+' + match[1];
          num = fullPhone.slice(match[0].length);
        }
      } else if (fullPhone.startsWith('91') && fullPhone.length > 2) {
        code = '91';
        num = fullPhone.slice(2);
      } else {
        code = '91';
        num = fullPhone;
      }
      setCountryCode(code);
      setPhoneNumber(num);

      let fullAlt = enquiry.alternatePhone || '';
      let altCode = '91';
      let altNum = '';
      if (fullAlt === '91' || !fullAlt) {
        altCode = '91';
        altNum = '';
      } else if (fullAlt.startsWith('+')) {
        const match = fullAlt.match(/^\+(\d{1,4})/);
        if (match) {
          altCode = '+' + match[1];
          altNum = fullAlt.slice(match[0].length);
        }
      } else if (fullAlt.startsWith('91') && fullAlt.length > 2) {
        altCode = '91';
        altNum = fullAlt.slice(2);
      } else {
        altCode = '91';
        altNum = fullAlt;
      }
      setAltCountryCode(altCode);
      setAltPhoneNumber(altNum);
    } else {
      setFormData({
        customerName: '',
        phone: '91',
        email: '',
        source: '',
        projectRequirement: '',
        status: 'NEW',
        assignedToId: '',
        branchId: '',
        alternatePhone: '91',
        careOf: '',
        secondContact: '',
        secondContactName: '',
        notes: '',
      });
      setCountryCode('91');
      setPhoneNumber('');
      setAltCountryCode('91');
      setAltPhoneNumber('');
    }
  }, [enquiry]);

  const fetchBranches = async () => {
    try {
      const response = await axios.get('/company/branches');
      const data = response.data.data || response.data;
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch branches:', err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let val = value;
    if (['phone', 'alternatePhone', 'secondContact'].includes(name)) {
      val = val.replace(/[a-zA-Z]/g, '');
    }
    setFormData(prev => ({
      ...prev,
      [name]: val
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const combinedPhone = `${countryCode}${phoneNumber}`;
    const combinedAlternatePhone = altPhoneNumber ? `${altCountryCode}${altPhoneNumber}` : '';

    // Validate
    const errs = {};
    if (!/^[0-9+\-\s()]{7,20}$/.test(combinedPhone)) errs.phone = 'Please enter a valid phone number (7-20 digits)';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errs.email = 'Invalid email format';
    if (isCompanyAdmin() && !formData.branchId) errs.branchId = 'Branch is required';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) {
      toast.error(Object.values(errs)[0]);
      if (errs.phone && phoneRef.current) {
        phoneRef.current.focus();
      }
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        phone: combinedPhone,
        alternatePhone: combinedAlternatePhone,
        assignedToId: formData.assignedToId ? parseInt(formData.assignedToId) : null,
        branchId: formData.branchId ? parseInt(formData.branchId) : null
      };

      if (enquiry) {
        await axios.put(`/enquiries/${enquiry.id}`, submitData);
      } else {
        await axios.post('/enquiries', submitData);
      }
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save enquiry');
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{enquiry ? 'Edit Enquiry' : 'Create Enquiry'}</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {isCompanyAdmin() && (
            <div className="form-group">
              <label htmlFor="branchId">Branch *</label>
              <select id="branchId" name="branchId" value={formData.branchId}
                onChange={handleChange} disabled={loading} className={`form-select${fieldErrors.branchId ? ' error' : ''}`} required={isCompanyAdmin()}>
                <option value="">Select Branch</option>
                {branches.map(b => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              {fieldErrors.branchId && <span className="error-message">{fieldErrors.branchId}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="customerName">Customer Name *</label>
            <input
              type="text"
              id="customerName"
              name="customerName"
              value={formData.customerName}
              onChange={handleChange}
              required
              disabled={loading}
              className="form-input"
              maxLength="100"
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone *</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                id="countryCode"
                name="countryCode"
                placeholder="Code"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value.replace(/[^0-9+]/g, ''))}
                disabled={loading}
                className={`form-input${fieldErrors.phone ? ' error' : ''}`}
                style={{ width: '80px', flexShrink: 0 }}
                maxLength="5"
                required
              />
              <input
                type="tel"
                id="phone"
                name="phone"
                ref={phoneRef}
                placeholder="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={loading}
                className={`form-input${fieldErrors.phone ? ' error' : ''}`}
                style={{ flexGrow: 1 }}
                maxLength="15"
                required
              />
            </div>
            {fieldErrors.phone
              ? <span className="error-message">{fieldErrors.phone}</span>
              : <small className="form-hint">Indian country code default is 91</small>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              disabled={loading}
              className={`form-input${fieldErrors.email ? ' error' : ''}`}
              maxLength="100"
            />
            {fieldErrors.email && <span className="error-message">{fieldErrors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="source">Source</label>
            <select
              id="source"
              name="source"
              value={formData.source}
              onChange={handleChange}
              disabled={loading}
              className="form-select"
            >
              <option value="">Select Source</option>
              <option value="Website">Website</option>
              <option value="Walk-in">Walk-in</option>
              <option value="Facebook">Facebook</option>
              <option value="WhatsApp">WhatsApp</option>
              <option value="Instagram">Instagram</option>
              <option value="Other Platform">Other Platform</option>
              <option value="Friend Referral">Friend Referral</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="projectRequirement">Project Requirement *</label>
            <textarea
              id="projectRequirement"
              name="projectRequirement"
              value={formData.projectRequirement}
              onChange={handleChange}
              required
              disabled={loading}
              className="form-textarea"
              rows="4"
              placeholder="Describe the project requirements..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="status">Status *</label>
            <select
              id="status"
              name="status"
              value={formData.status}
              onChange={handleChange}
              required
              disabled={loading}
              className="form-select"
            >
              <option value="NEW">New</option>
              <option value="FOLLOWUP">Follow-up</option>
              <option value="CUSTOMER_CONVERTED">Customer Converted</option>
              <option value="PROJECT_CONVERTED">Project Converted</option>
              <option value="LOST">Lost</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="assignedToId">Assign To</label>
            <select
              id="assignedToId"
              name="assignedToId"
              value={formData.assignedToId}
              onChange={handleChange}
              disabled={loading}
              className="form-select"
            >
              <option value="">Unassigned</option>
              {users.map(user => (
                <option key={user.id} value={user.id}>
                  {user.name} ({user.role})
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="alternatePhone">Alternate Phone</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                id="altCountryCode"
                name="altCountryCode"
                placeholder="Code"
                value={altCountryCode}
                onChange={(e) => setAltCountryCode(e.target.value.replace(/[^0-9+]/g, ''))}
                disabled={loading}
                className={`form-input${fieldErrors.alternatePhone ? ' error' : ''}`}
                style={{ width: '80px', flexShrink: 0 }}
                maxLength="5"
              />
              <input
                type="tel"
                id="alternatePhone"
                name="alternatePhone"
                placeholder="Alternate Number"
                value={altPhoneNumber}
                onChange={(e) => setAltPhoneNumber(e.target.value.replace(/[^0-9]/g, ''))}
                disabled={loading}
                className={`form-input${fieldErrors.alternatePhone ? ' error' : ''}`}
                style={{ flexGrow: 1 }}
                maxLength="15"
              />
            </div>
            {fieldErrors.alternatePhone && fieldErrors.alternatePhone !== 'Invalid phone number' && (
              <span className="error-message">{fieldErrors.alternatePhone}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="careOf">Care Of (C/O)</label>
            <input type="text" id="careOf" name="careOf"
              value={formData.careOf} onChange={handleChange}
              disabled={loading} className="form-input" maxLength="100"
              placeholder="e.g. c/o John Smith" />
          </div>

          <div className="form-group">
            <label htmlFor="secondContact">Second Contact Number</label>
            <input type="tel" id="secondContact" name="secondContact"
              value={formData.secondContact} onChange={handleChange}
              disabled={loading} className="form-input" maxLength="20" />
          </div>

          <div className="form-group">
            <label htmlFor="secondContactName">Second Contact Name</label>
            <input type="text" id="secondContactName" name="secondContactName"
              value={formData.secondContactName} onChange={handleChange}
              disabled={loading} className="form-input" maxLength="100" />
          </div>

          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea id="notes" name="notes" value={formData.notes}
              onChange={handleChange} disabled={loading}
              className="form-textarea" rows="2" />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : (enquiry ? 'Update Enquiry' : 'Create Enquiry')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EnquiryFormModal;
